import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  Switch,
  Pagination,
  TextField,
  Box,
  InputLabel,
  Paper,
  Backdrop,
  CircularProgress,
  Chip,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Divider,
  Badge,
  Grid,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import DownloadIcon from "@mui/icons-material/Download";
import GetAppIcon from "@mui/icons-material/GetApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import PaymentIcon from "@mui/icons-material/Payment";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment"; // for External PO
import MergeTypeIcon from "@mui/icons-material/MergeType";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { jsPDF } from 'jspdf';

import client from "../../../../API";
import { StyledTableRow, StyledTableCell } from "../../../../Utils/styledcell";
import InvoiceDialog from "../invoicedialog";
import { invoiceStatuses } from "../../../../Utils/constants";
import { useIsMounted } from "../../../../Hooks/use-is-mounted";
import { generateBlitzPayPDF } from "../../invoicing/downloadPdf/generateBlitzPayPDF";
import {
  StyleButtonContainter,
  StyleButton,
  StyleContentWithNavBar,
} from "../../../../Utils/ThemedStyles";

const InvoiceDetailsView = () => {
  const isMounted = useIsMounted();
  const queryClient = useQueryClient();

  // State
  const [selectedIds, setSelectedIds] = useState([]);
  const [isInvoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("created_desc");
  const [page, setPage] = useState(0);
  const itemsPerPage = 20;
  const [downloadingPdfs, setDownloadingPdfs] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [usePagination, setUsePagination] = useState(true);
  const [showFilters, setShowFilters] = useState(true);

  // External PO Edit State
  const [externalPoDialogOpen, setExternalPoDialogOpen] = useState(false);
  const [externalPoInvoice, setExternalPoInvoice] = useState(null);
  const [externalPoInput, setExternalPoInput] = useState("");

  // BlitzPay Repayment State
  const [repaymentDialogOpen, setRepaymentDialogOpen] = useState(false);
  const [selectedRepaymentInvoice, setSelectedRepaymentInvoice] = useState(null);
  const [repaymentTransactionId, setRepaymentTransactionId] = useState("");
  const [repaymentPaymentMethod, setRepaymentPaymentMethod] = useState("");
  const [repaymentNotes, setRepaymentNotes] = useState("");
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeConflict, setMergeConflict] = useState(null);

  // Build query params for backend
  const buildQueryParams = () => {
    const params = usePagination
      ? { page, rows: itemsPerPage }
      : { page: 0, rows: 9999, all: 'true' };

    if (statusFilter) params.status = statusFilter;
    if (companyFilter) params.company_id = companyFilter;
    if (userFilter) params.user_id = userFilter;
    if (teamFilter) params.team_id = teamFilter;
    if (searchQuery) params.search = searchQuery;
    if (sortOption) params.sort = sortOption;

    return params;
  };

  const {
    data,
    refetch: fetchInvoices,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["invoices", page, itemsPerPage, usePagination, statusFilter, companyFilter, userFilter, teamFilter, searchQuery, sortOption],
    queryFn: () => {
      const params = buildQueryParams();
      return client.invoices.listAdmin(params);
    },
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  useEffect(() => {
    setSelectedIds([]);
  }, [data]);

  useEffect(() => {
    if (error) {
      console.error("Error fetching invoices:", error);
      if (error.code !== 'NETWORK_ERROR' && error.code !== 'ERR_NETWORK') {
        toast.error(
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error.message ||
          'Failed to fetch invoices'
        );
      }
    }
  }, [error]);

  /* ------------------------------------------------------------------ */
  /* External PO Mutation                                                */
  /* ------------------------------------------------------------------ */
  const updateExternalPoMutation = useMutation(
    ({ invoiceId, externalPoNumber }) =>
      client.invoices.updateExternalPO(invoiceId, {
        external_po_number: externalPoNumber,
      }),
    {
      onSuccess: () => {
        toast.success(
          `External PO updated for invoice #${externalPoInvoice?.id}`
        );
        queryClient.invalidateQueries(["invoices"]);
        fetchInvoices();
        handleCloseExternalPoDialog();
      },
      onError: (error) => {
        toast.error(
          `Failed to update External PO: ${error?.response?.data?.error || error.message}`,
          { autoClose: 6000 }
        );
      },
    }
  );

  /* ------------------------------------------------------------------ */
  /* External PO Handlers                                                */
  /* ------------------------------------------------------------------ */
  const handleOpenExternalPoDialog = (invoice) => {
    setExternalPoInvoice(invoice);
    setExternalPoInput(invoice.external_po_number || "");
    setExternalPoDialogOpen(true);
  };

  const handleCloseExternalPoDialog = () => {
    setExternalPoDialogOpen(false);
    setExternalPoInvoice(null);
    setExternalPoInput("");
  };

  const handleSaveExternalPo = () => {
    if (!externalPoInvoice) return;
    updateExternalPoMutation.mutate({
      invoiceId: externalPoInvoice.id,
      externalPoNumber: externalPoInput.trim() || null,
    });
  };

  /* ------------------------------------------------------------------ */
  /* BlitzPay Repayment Mutations                                       */
  /* ------------------------------------------------------------------ */
  const processRepaymentMutation = useMutation(
    (data) => client.blitzpayRepayments.processRepayment(data),
    {
      onSuccess: (data) => {
        toast.success(
          `Repayment processed! Restored $${data.amount_restored.toFixed(2)} to ${data.spending_source_name}`,
          { autoClose: 5000 }
        );
        queryClient.invalidateQueries(["invoices"]);
        fetchInvoices();
        handleCloseRepaymentDialog();
      },
      onError: (error) => {
        toast.error(`Repayment failed: ${error.message}`, { autoClose: 7000 });
      },
    }
  );

  const processBatchRepaymentMutation = useMutation(
    (data) => client.blitzpayRepayments.processBatch(data),
    {
      onSuccess: (data) => {
        if (data.success) {
          toast.success(
            `Batch processed! ${data.processed_count} repayments completed. Total restored: $${data.total_amount_restored.toFixed(2)}`,
            { autoClose: 6000 }
          );
        } else {
          toast.warning(
            `Partial success: ${data.processed_count} succeeded, ${data.failed_count} failed`,
            { autoClose: 6000 }
          );
        }
        queryClient.invalidateQueries(["invoices"]);
        fetchInvoices();
        setSelectedIds([]);
      },
      onError: (error) => {
        toast.error(`Batch processing failed: ${error.message}`, { autoClose: 7000 });
      },
    }
  );

  const mergeBlitzPayMutation = useMutation(
    (data) => client.invoices.mergeBlitzPayAdmin(data),
    {
      onSuccess: (result) => {
        try {
          generateBlitzPayPDF(
            result?.merged_invoice,
            result?.blitzpay_profile,
            [],
            result?.fallback_billed_to || {},
          );
        } catch (error) {
          toast.error("Merged invoice was prepared, but PDF generation failed. Please retry with the same funded invoices.", {
            autoClose: 7000,
          });
          return;
        }

        toast.success(result?.message || "Merged BlitzPay PDF downloaded successfully");
        if (result?.amounts_changed?.length) {
          toast.warn(
            `Invoice amounts changed since the original export (invoices ${result.amounts_changed.join(", ")}). ` +
            "The regenerated PDF uses the current amounts.",
            { autoClose: 10000 }
          );
        }
        queryClient.invalidateQueries(["invoices"]);
        fetchInvoices();
        setSelectedIds([]);
        setMergeConflict(null);
        setMergeDialogOpen(false);
      },
      onError: (error) => {
        const conflict = error?.response?.data?.conflict;
        if (conflict?.merge_group_id) {
          setMergeConflict(conflict);
          return;
        }
        toast.error(
          error?.response?.data?.error ||
          error?.message ||
          "Failed to generate merged BlitzPay PDF",
          { autoClose: 7000 }
        );
      },
    }
  );

  const dissolveBlitzPayMergeMutation = useMutation(
    (data) => client.invoices.dissolveBlitzPayMergeAdmin(data),
    {
      onSuccess: (result) => {
        toast.success(result?.message || "Merged export dissolved");
        setMergeConflict(null);
        queryClient.invalidateQueries(["invoices"]);
        fetchInvoices();
        // Retry the merge for the current selection now that the group is dissolved
        mergeBlitzPayMutation.mutate({ invoice_ids: selectedIds });
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.error ||
          error?.message ||
          "Failed to dissolve merged export",
          { autoClose: 7000 }
        );
      },
    }
  );

  /* ------------------------------------------------------------------ */
  /* BlitzPay Repayment Handlers                                        */
  /* ------------------------------------------------------------------ */
  const handleOpenRepaymentDialog = (invoice) => {
    setSelectedRepaymentInvoice(invoice);
    setRepaymentTransactionId("");
    setRepaymentPaymentMethod("");
    setRepaymentNotes("");
    setRepaymentDialogOpen(true);
  };

  const handleCloseRepaymentDialog = () => {
    setRepaymentDialogOpen(false);
    setSelectedRepaymentInvoice(null);
    setRepaymentTransactionId("");
    setRepaymentPaymentMethod("");
    setRepaymentNotes("");
  };

  const handleProcessSingleRepayment = async () => {
    if (!selectedRepaymentInvoice) return;

    if (!repaymentTransactionId.trim()) {
      toast.error("Transaction ID is required");
      return;
    }

    if (!repaymentPaymentMethod) {
      toast.error("Payment method is required");
      return;
    }

    const repaymentData = {
      invoice_id: selectedRepaymentInvoice.id,
      payment_amount: selectedRepaymentInvoice.amount_due,
      external_transaction_id: repaymentTransactionId.trim(),
      payment_method: repaymentPaymentMethod,
      notes: repaymentNotes.trim() || undefined,
    };

    await processRepaymentMutation.mutateAsync(repaymentData);
  };

  const handleProcessBatchRepayment = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select invoices to process");
      return;
    }

    const invoices = data?.invoices || [];
    const eligibleInvoices = invoices.filter(
      (invoice) =>
        selectedIds.includes(invoice.id) &&
        client.blitzpayRepayments.isEligible(invoice)
    );

    if (eligibleInvoices.length === 0) {
      toast.warning("No eligible BlitzPay invoices selected");
      return;
    }

    const defaultTransactionId = `BATCH_${Date.now()}`;
    const defaultPaymentMethod = "manual";

    const repayments = eligibleInvoices.map((invoice) => ({
      invoice_id: invoice.id,
      payment_amount: invoice.amount_due,
      external_transaction_id: `${defaultTransactionId}_INV_${invoice.id}`,
      payment_method: defaultPaymentMethod,
      notes: `Batch repayment processed on ${new Date().toISOString()}`,
    }));

    await processBatchRepaymentMutation.mutateAsync({ repayments });
  };

  const isInvoiceRepaymentEligible = (invoice) => {
    return client.blitzpayRepayments.isEligible(invoice);
  };

  const getSelectedRepaymentCount = () => {
    const invoices = data?.invoices || [];
    return invoices.filter(
      (invoice) =>
        selectedIds.includes(invoice.id) && isInvoiceRepaymentEligible(invoice)
    ).length;
  };

  const paymentMethods = client.blitzpayRepayments.getPaymentMethods();

  const normalizeClientEmail = (email) => (email || "").trim().toLowerCase();
  const normalizeClientName = (name) => (name || "").trim().toLowerCase().replace(/\s+/g, " ");

  const getInvoiceMergeEmail = (invoice) =>
    normalizeClientEmail(
      invoice?.client_email ||
      invoice?.custom_details?.billed_to_info?.email
    );

  const getInvoiceMergeClientName = (invoice) =>
    (invoice?.client_name ||
      invoice?.custom_details?.billed_to_info?.company_name ||
      invoice?.custom_details?.billed_to_info?.name ||
      "").trim();

  const getInvoiceMergeClientLabel = (invoice) =>
    getInvoiceMergeEmail(invoice) || getInvoiceMergeClientName(invoice);

  const getInvoiceMergeIssuerUserId = (invoice) =>
    invoice?.user_id || invoice?.user_info?.id || null;

  const isBlitzPayInvoice = (invoice) =>
    Boolean(
      invoice?.blitzpay ||
      invoice?.invoice_type === "blitzpay" ||
      invoice?.source_type === "blitzpay"
    );

  // Utility functions
  const getDaysBetweenDates = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start).getTime();
    const endDate = new Date(end).getTime();
    return Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  const getDaysLate = (invoice) => {
    if (!invoice.date_paid || !invoice.payment_term) return "N/A";
    const totalDays = getDaysBetweenDates(invoice.created_at, invoice.date_paid);
    const daysLate = totalDays - invoice.payment_term;
    return daysLate < 0 ? 0 : daysLate;
  };

  const getTotalDaysToPay = (invoice) => {
    if (!invoice.date_paid) return "N/A";
    return getDaysBetweenDates(invoice.created_at, invoice.date_paid);
  };

  // Fetch filter options
  const { data: filterOptions } = useQuery(
    ["invoiceFilters"],
    () => client.invoices.getFilters(),
    { staleTime: 10 * 60 * 1000, enabled: true }
  );

  const uniqueCompanies = filterOptions?.companies || [];
  const uniqueUsers = filterOptions?.users || [];
  const uniqueTeams = filterOptions?.teams || [];

  // CSV Export — includes external_po_number column
  const onExportToCsv = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select invoices to export");
      return;
    }

    setDownloadingCsv(true);
    try {
      const csvData = [];
      const headers = [
        'Invoice ID',
        'PO Number',
        'External PO Number',   // ← added
        'Company Name',
        'User Name',
        'Teams',
        'Creator ID',
        'Subtotal',
        'Fee (%)',
        'Payable Amount',
        'Date Issued',
        'Date Paid',
        'Approved At',
        'Status',
        'Type',
        'Payment Term',
        'Total Days to Pay',
        'Days Late',
        'Client Name',
        'Client Email',
        'Notes',
        'Blitzpay',
        'Interest Rate',
        'Payout Date',
        'Funded By',
        'Funded Date',
        'Source Type',
        'Repayment Eligible',
      ];

      csvData.push(headers);

      const invoices = data?.invoices || [];
      const invoicesToExport = invoices.filter(invoice =>
        selectedIds.includes(invoice.id)
      );

      invoicesToExport.forEach(invoice => {
        const payableAmount = invoice.amount_due * (1 - invoice.fee_percentage / 100);
        const daysLate = getDaysLate(invoice);
        const totalDaysToPay = getTotalDaysToPay(invoice);
        const repaymentEligible = isInvoiceRepaymentEligible(invoice);

        const row = [
          invoice.id,
          invoice.po_number || '',
          invoice.external_po_number || '',   // ← added
          invoice.company_info?.name || '',
          invoice.user_info ? `${invoice.user_info.first_name} ${invoice.user_info.last_name}` : '',
          invoice.team_info?.map(team => team.name).join('; ') || '',
          invoice.creator_id || '',
          invoice.amount_due?.toFixed(2) || '0.00',
          invoice.fee_percentage?.toFixed(0) || '0',
          payableAmount.toFixed(2),
          invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : '',
          invoice.date_paid ? new Date(invoice.date_paid).toLocaleDateString() : '',
          invoice.approved_at ? new Date(invoice.approved_at).toLocaleDateString() : '',
          invoice.status || '',
          invoice.invoice_type || '',
          invoice.payment_term || '',
          totalDaysToPay,
          daysLate,
          invoice.client_name || '',
          invoice.client_email || '',
          invoice.notes || '',
          invoice.blitzpay ? 'Yes' : 'No',
          invoice.interest_rate || '',
          invoice.payout_date ? new Date(invoice.payout_date).toLocaleDateString() : '',
          invoice.funded_by || '',
          invoice.funded_date ? new Date(invoice.funded_date).toLocaleDateString() : '',
          invoice.source_type || '',
          repaymentEligible ? 'Yes' : 'No',
        ];
        csvData.push(row);
      });

      const csvContent = csvData.map(row =>
        row.map(field =>
          typeof field === 'string' && field.includes(',')
            ? `"${field}"`
            : field
        ).join(',')
      ).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `invoices_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${selectedIds.length} invoices to CSV`);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      toast.error('Error exporting to CSV. Please try again.');
    } finally {
      setDownloadingCsv(false);
    }
  };

  // Delete selected invoices
  const onDeleteInvoices = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select invoices to delete");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} invoice(s)?`)) {
      return;
    }

    try {
      await client.invoices.delete({ invoiceIds: selectedIds });
      toast.success(`Deleted ${selectedIds.length} invoice(s)`);
      setSelectedIds([]);
      fetchInvoices();
    } catch (error) {
      console.error("Error deleting invoices", error);
      toast.error("Error deleting invoices. Please try again.");
    }
  };

  // Bulk PDF Download
  const onDownloadPdfs = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Please select invoices to download");
      return;
    }

    setDownloadingPdfs(true);
    try {
      const response = await client.invoices.fetchBatch(selectedIds);
      const invoicesData = response.invoices;

      const invoicesMap = {};
      invoicesData.forEach(invoice => {
        invoicesMap[invoice.id] = invoice;
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      let isFirstPage = true;

      for (const invoiceId of selectedIds) {
        try {
          const invoice = invoicesMap[invoiceId];
          if (!invoice) continue;

          if (!isFirstPage) pdf.addPage();
          isFirstPage = false;

          pdf.setFontSize(20);
          pdf.text(`Invoice #${invoice.po_number || invoiceId}`, 20, 30);

          pdf.setFontSize(12);
          let yPosition = 50;

          pdf.text(`Amount: $${(invoice.amount_due || 0).toFixed(2)}`, 20, yPosition);
          yPosition += 10;

          pdf.text(`Status: ${invoice.status || 'N/A'}`, 20, yPosition);
          yPosition += 10;

          pdf.text(`Created: ${new Date(invoice.created_at).toLocaleDateString()}`, 20, yPosition);
          yPosition += 10;

          if (invoice.external_po_number) {
            pdf.text(`External PO: ${invoice.external_po_number}`, 20, yPosition);
            yPosition += 10;
          }

          if (invoice.client_name) {
            pdf.text(`Client: ${invoice.client_name}`, 20, yPosition);
            yPosition += 10;
          }

          if (invoice.notes) {
            pdf.text(`Notes: ${invoice.notes}`, 20, yPosition);
            yPosition += 10;
          }
        } catch (err) {
          console.error(`Error processing invoice ${invoiceId}:`, err);
        }
      }

      pdf.save(`invoices_bulk_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(`Downloaded ${selectedIds.length} invoices as PDF`);
    } catch (error) {
      console.error("Error generating bulk PDFs:", error);
      toast.error("Error generating PDFs. Please try again.");
    } finally {
      setDownloadingPdfs(false);
    }
  };

  // Dialog handlers
  const handleDialogOpen = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceDialogOpen(true);
  };

  const handleDialogClose = (changed) => {
    setInvoiceDialogOpen(false);
    setEditingInvoice(null);
    if (changed) fetchInvoices();
  };

  const handleLink = (invoice) => {
    const invoiceUrl = `${window.location.origin}/invoicing/${invoice.id}`;
    window.open(invoiceUrl, "_blank", "noopener,noreferrer");
  };

  const handleShowApproval = (approvalInfo) => {
    setSelectedApproval(approvalInfo);
    setApprovalDialogOpen(true);
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedIds((prevSelected) =>
      prevSelected.includes(invoiceId)
        ? prevSelected.filter((id) => id !== invoiceId)
        : [...prevSelected, invoiceId]
    );
  };

  const handleClearFilters = () => {
    setStatusFilter("");
    setCompanyFilter("");
    setUserFilter("");
    setTeamFilter("");
    setSearchQuery("");
    setSortOption("created_desc");
    setPage(0);
  };

  const hasActiveFilters = statusFilter || companyFilter || userFilter || teamFilter || searchQuery;

  const invoices = data?.invoices || [];
  const totalInvoices = data?.total || 0;
  const totalAmount = invoices.reduce((sum, inv) => sum + (inv.amount_due || 0), 0);
  const selectedRepaymentCount = getSelectedRepaymentCount();
  const selectedInvoices = useMemo(
    () => invoices.filter((invoice) => selectedIds.includes(invoice.id)),
    [invoices, selectedIds]
  );
  const selectedMergeTotal = selectedInvoices.reduce(
    (sum, invoice) => sum + (invoice.amount_due || 0),
    0
  );
  const selectedClientEmails = [
    ...new Set(
      selectedInvoices
        .map((invoice) => getInvoiceMergeEmail(invoice))
        .filter(Boolean)
    ),
  ];
  const selectedClientNames = [
    ...new Set(
      selectedInvoices
        .map((invoice) => normalizeClientName(getInvoiceMergeClientName(invoice)))
        .filter(Boolean)
    ),
  ];
  const selectedExternalPoNumbers = [
    ...new Set(
      selectedInvoices
        .map((invoice) =>
          typeof invoice?.external_po_number === "string"
            ? invoice.external_po_number.trim()
            : invoice?.external_po_number
        )
        .filter(Boolean)
    ),
  ];
  const selectedIssuerUserIds = [
    ...new Set(
      selectedInvoices
        .map((invoice) => getInvoiceMergeIssuerUserId(invoice))
        .filter(Boolean)
    ),
  ];
  const selectedStatuses = [
    ...new Set(selectedInvoices.map((invoice) => invoice.status).filter(Boolean)),
  ];
  const selectedClientLabel = selectedInvoices.length > 0
    ? getInvoiceMergeClientLabel(selectedInvoices[0]) || "Mixed"
    : "";
  const selectedHasMissingClientIdentity = selectedInvoices.some(
    (invoice) => !getInvoiceMergeClientLabel(invoice)
  );
  const selectedHasMissingIssuer = selectedInvoices.some(
    (invoice) => !getInvoiceMergeIssuerUserId(invoice)
  );
  const mergeValidationError =
    selectedIds.length < 2
      ? "Select at least 2 invoices to combine."
      : selectedInvoices.length !== selectedIds.length
        ? "Some selected invoices are not loaded on this page. Refresh or select invoices from the current page."
        : selectedInvoices.some((invoice) => !isBlitzPayInvoice(invoice))
          ? "Only BlitzPay invoices can be combined."
          : selectedInvoices.some((invoice) => !["funded", "paid"].includes(invoice.status))
            ? "Only funded BlitzPay invoices can be combined (paid ones only to re-download an existing export)."
            : selectedHasMissingIssuer
              ? "Every selected invoice must belong to an issuing BlitzPay account."
              : selectedIssuerUserIds.length !== 1
                ? "Selected invoices must belong to the same issuing BlitzPay account."
              : selectedHasMissingClientIdentity
                ? "Every selected invoice must have a client email or company name."
              : selectedClientEmails.length > 1
                ? "Selected invoices must have the same client email."
                : selectedClientEmails.length === 0 && selectedClientNames.length !== 1
                  ? "Selected invoices must belong to the same client."
                  : selectedExternalPoNumbers.length > 1
                    ? "Selected invoices must have the same external PO number."
                  : "";

  const handleMergeBlitzPayInvoices = () => {
    setMergeConflict(null);
    mergeBlitzPayMutation.mutate({ invoice_ids: selectedIds });
  };

  const handleDissolveAndRemerge = () => {
    if (!mergeConflict?.merge_group_id) return;
    dissolveBlitzPayMergeMutation.mutate({ merge_group_id: mergeConflict.merge_group_id });
  };

  const handleCloseMergeDialog = () => {
    if (mergeBlitzPayMutation.isLoading || dissolveBlitzPayMergeMutation.isLoading) return;
    setMergeConflict(null);
    setMergeDialogOpen(false);
  };

  return (
    <Box sx={StyleContentWithNavBar}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isFetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Invoice Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all invoices across your organization
            </Typography>
          </Box>
          <FormControlLabel
            label="Pagination"
            checked={usePagination}
            onChange={() => {
              setUsePagination((prev) => !prev);
              setPage(0);
            }}
            control={<Switch color="primary" />}
          />
        </Stack>

        {/* Stats Cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>Total Invoices</Typography>
                    <Typography variant="h4" fontWeight={700}>{totalInvoices.toLocaleString()}</Typography>
                  </Box>
                  <ReceiptIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>Total Value</Typography>
                    <Typography variant="h4" fontWeight={700}>
                      ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>On This Page</Typography>
                    <Typography variant="h4" fontWeight={700}>{invoices.length}</Typography>
                  </Box>
                  <BusinessIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>Selected</Typography>
                    <Typography variant="h4" fontWeight={700}>{selectedIds.length}</Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Bar */}
        <Card sx={{ mb: 2, background: 'linear-gradient(to right, #f8f9fa, #ffffff)' }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={onDeleteInvoices}
                  startIcon={<DeleteIcon />}
                  disabled={!selectedIds.length}
                  color="error"
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Delete ({selectedIds.length})
                </Button>

                <Button
                  variant="contained"
                  onClick={onDownloadPdfs}
                  startIcon={downloadingPdfs ? <CircularProgress size={16} /> : <DownloadIcon />}
                  disabled={!selectedIds.length || downloadingPdfs}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}
                >
                  {downloadingPdfs ? 'Generating...' : `PDF (${selectedIds.length})`}
                </Button>

                <Button
                  variant="contained"
                  onClick={onExportToCsv}
                  startIcon={downloadingCsv ? <CircularProgress size={16} /> : <GetAppIcon />}
                  disabled={!selectedIds.length || downloadingCsv}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                  }}
                >
                  {downloadingCsv ? 'Exporting...' : `CSV (${selectedIds.length})`}
                </Button>

                <Button
                  variant="contained"
                  onClick={() => setMergeDialogOpen(true)}
                  startIcon={<MergeTypeIcon />}
                  disabled={selectedIds.length < 2 || mergeBlitzPayMutation.isLoading}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: '#f97316',
                    '&:hover': {
                      backgroundColor: '#ea580c',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#fdba74',
                      color: 'rgba(255, 255, 255, 0.8)',
                    },
                  }}
                >
                  Combine BlitzPay ({selectedIds.length})
                </Button>

                {selectedRepaymentCount > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleProcessBatchRepayment}
                    startIcon={
                      processBatchRepaymentMutation.isLoading
                        ? <CircularProgress size={16} />
                        : <PaymentIcon />
                    }
                    disabled={processBatchRepaymentMutation.isLoading}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                    }}
                  >
                    {processBatchRepaymentMutation.isLoading
                      ? 'Processing...'
                      : `Repayments (${selectedRepaymentCount})`}
                  </Button>
                )}

                <Box sx={{ flexGrow: 1 }} />

                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
              </Stack>

              {hasActiveFilters && (
                <Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      Active Filters:
                    </Typography>
                    {statusFilter && (
                      <Chip label={`Status: ${statusFilter}`} onDelete={() => setStatusFilter("")} size="small" color="primary" sx={{ textTransform: 'capitalize' }} />
                    )}
                    {companyFilter && (
                      <Chip label={`Company: ${uniqueCompanies.find(c => c.id === parseInt(companyFilter))?.name || companyFilter}`} onDelete={() => setCompanyFilter("")} size="small" color="primary" />
                    )}
                    {userFilter && (
                      <Chip label={`User: ${uniqueUsers.find(u => u.id === parseInt(userFilter))?.first_name || userFilter}`} onDelete={() => setUserFilter("")} size="small" color="primary" />
                    )}
                    {teamFilter && (
                      <Chip label={`Team: ${uniqueTeams.find(t => t.id === parseInt(teamFilter))?.name || teamFilter}`} onDelete={() => setTeamFilter("")} size="small" color="primary" />
                    )}
                    {searchQuery && (
                      <Chip label={`Search: "${searchQuery}"`} onDelete={() => setSearchQuery("")} size="small" color="primary" />
                    )}
                    <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters} sx={{ ml: 1, textTransform: 'none' }}>
                      Clear All
                    </Button>
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Filters Section */}
        {showFilters && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>Filters & Search</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={3}>
                  <TextField
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                    placeholder="Search invoices..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start"><SearchIcon /></InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    select fullWidth
                    value={statusFilter || "all"}
                    onChange={(e) => { setStatusFilter(e.target.value === "all" ? "" : e.target.value); setPage(0); }}
                    label="Status"
                    SelectProps={{ sx: { textTransform: "capitalize" } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {invoiceStatuses.map((status) => (
                      <MenuItem key={status} value={status} sx={{ textTransform: "capitalize" }}>{status}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    select fullWidth
                    value={companyFilter || "all"}
                    onChange={(e) => { setCompanyFilter(e.target.value === "all" ? "" : e.target.value); setPage(0); }}
                    label="Company"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="all">All Companies</MenuItem>
                    {uniqueCompanies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    select fullWidth
                    value={userFilter || "all"}
                    onChange={(e) => { setUserFilter(e.target.value === "all" ? "" : e.target.value); setPage(0); }}
                    label="User"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    {uniqueUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>{user.first_name} {user.last_name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    select fullWidth
                    value={teamFilter || "all"}
                    onChange={(e) => { setTeamFilter(e.target.value === "all" ? "" : e.target.value); setPage(0); }}
                    label="Team"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="all">All Teams</MenuItem>
                    {uniqueTeams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth>
                    <InputLabel>Sort by</InputLabel>
                    <Select
                      value={sortOption}
                      label="Sort by"
                      onChange={(e) => { setSortOption(e.target.value); setPage(0); }}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="created_desc">Date: Newest First</MenuItem>
                      <MenuItem value="created_asc">Date: Oldest First</MenuItem>
                      <MenuItem value="amount_desc">Amount: High to Low</MenuItem>
                      <MenuItem value="amount_asc">Amount: Low to High</MenuItem>
                      <MenuItem value="days_late_desc">Days Late: Most</MenuItem>
                      <MenuItem value="days_late_asc">Days Late: Least</MenuItem>
                      <MenuItem value="terms_desc">Payment Terms: High to Low</MenuItem>
                      <MenuItem value="terms_asc">Payment Terms: Low to High</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          <AlertTitle>Error Loading Invoices</AlertTitle>
          {error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to load invoices. Please refresh the page.'}
        </Alert>
      )}

      {/* Invoices Table */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < invoices.length}
                    checked={selectedIds.length === invoices.length && invoices.length > 0}
                    onChange={(event) =>
                      setSelectedIds(event.target.checked ? invoices.map((inv) => inv.id) : [])
                    }
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Company/User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>PO Number</TableCell>
                {/* ↓ NEW COLUMN */}
                <TableCell sx={{ fontWeight: 700 }}>External PO</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fee %</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payable</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Paid</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Approval</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Term</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Days</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Late</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>BlitzPay</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data?.invoices ? (
                <TableRow>
                  <TableCell colSpan={19} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading invoices...</Typography>
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={19} align="center" sx={{ py: 8 }}>
                    <ReceiptIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">No invoices found</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters ? 'Try adjusting your filters' : 'No invoices available'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => {
                  const payableAmount = invoice.amount_due * (1 - invoice.fee_percentage / 100);
                  const isRepaymentEligible = isInvoiceRepaymentEligible(invoice);

                  return (
                    <StyledTableRow key={invoice.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          onChange={() => handleSelectInvoice(invoice.id)}
                          checked={selectedIds.includes(invoice.id)}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>#{invoice.id}</Typography>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.5}>
                          {invoice.company_info && (
                            <Chip icon={<BusinessIcon />} label={invoice.company_info.name} size="small" variant="outlined"
                              sx={{ borderRadius: 1, bgcolor: 'primary.50', borderColor: 'primary.200' }} />
                          )}
                          {invoice.user_info && (
                            <Chip icon={<PersonIcon />} label={`${invoice.user_info.first_name} ${invoice.user_info.last_name}`}
                              size="small" variant="outlined" sx={{ borderRadius: 1, bgcolor: 'secondary.50', borderColor: 'secondary.200' }} />
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.5}>
                          {invoice.team_info && invoice.team_info.length > 0 ? (
                            invoice.team_info.map((team) => (
                              <Chip key={team.id} icon={<GroupIcon />} label={team.name} size="small" variant="outlined"
                                sx={{ borderRadius: 1, bgcolor: 'info.50', borderColor: 'info.200' }} />
                            ))
                          ) : (
                            <Typography variant="caption" color="textSecondary">-</Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {invoice.po_number || '-'}
                        </Typography>
                      </TableCell>

                      {/* ── External PO column ── */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {invoice.external_po_number ? (
                            <Tooltip title={`External PO: ${invoice.external_po_number}`}>
                              <Chip
                                icon={<AssignmentIcon />}
                                label={invoice.external_po_number}
                                size="small"
                                variant="outlined"
                                sx={{
                                  borderRadius: 1,
                                  bgcolor: 'warning.50',
                                  borderColor: 'warning.300',
                                  color: 'warning.dark',
                                  fontWeight: 600,
                                  maxWidth: 140,
                                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="textSecondary">—</Typography>
                          )}
                          <Tooltip title={invoice.external_po_number ? "Edit External PO" : "Add External PO"}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenExternalPoDialog(invoice)}
                              sx={{
                                bgcolor: invoice.external_po_number ? 'warning.50' : 'grey.100',
                                '&:hover': { bgcolor: invoice.external_po_number ? 'warning.100' : 'grey.200' },
                              }}
                            >
                              <AssignmentIcon fontSize="small" color={invoice.external_po_number ? "warning" : "disabled"} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          ${invoice.amount_due.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip label={`${invoice.fee_percentage.toFixed(0)}%`} size="small" sx={{ bgcolor: 'grey.100', fontWeight: 600 }} />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          ${payableAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{new Date(invoice.created_at).toLocaleDateString()}</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {invoice.date_paid ? new Date(invoice.date_paid).toLocaleDateString() : '-'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip
                            label={invoice.status}
                            color={
                              invoice.status === 'paid' ? 'success' :
                              invoice.status === 'approved' ? 'info' :
                              invoice.status === 'pending' ? 'warning' :
                              invoice.status === 'funded' ? 'secondary' : 'default'
                            }
                            size="small"
                            sx={{ textTransform: 'capitalize', fontWeight: 600, borderRadius: 1 }}
                          />
                          {isRepaymentEligible && (
                            <Tooltip title="Eligible for repayment">
                              <PaymentIcon fontSize="small" color="success" />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        {invoice.approval_info ? (
                          <Tooltip title="View approval details">
                            <IconButton size="small" onClick={() => handleShowApproval(invoice.approval_info)} color="success">
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">-</Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip label={invoice.invoice_type} size="small" variant="outlined"
                            sx={{ textTransform: 'capitalize', borderRadius: 1 }} />
                          {invoice.external_metadata?.admin_merge_export?.merge_group_id && (
                            <Tooltip
                              title={`On merged export MERGED-${String(invoice.external_metadata.admin_merge_export.merge_group_id).slice(0, 8).toUpperCase()} with invoices ${(invoice.external_metadata.admin_merge_export.source_invoice_ids || []).join(", ")}`}
                            >
                              <Chip
                                label="Merged"
                                size="small"
                                color="warning"
                                sx={{ borderRadius: 1, fontWeight: 600 }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {invoice.payment_term ? `${invoice.payment_term}d` : '-'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">{getTotalDaysToPay(invoice)}</Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="body2"
                          color={getDaysLate(invoice) > 0 ? 'error.main' : 'text.primary'}
                          fontWeight={getDaysLate(invoice) > 0 ? 600 : 400}
                        >
                          {getDaysLate(invoice)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={invoice.blitzpay ? 'Yes' : 'No'}
                          size="small"
                          color={invoice.blitzpay ? 'secondary' : 'default'}
                          sx={{ borderRadius: 1, fontWeight: 600 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Edit invoice">
                            <IconButton size="small" onClick={() => handleDialogOpen(invoice)}
                              sx={{ bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="View public link">
                            <IconButton size="small" onClick={() => handleLink(invoice)}
                              sx={{ bgcolor: 'info.50', '&:hover': { bgcolor: 'info.100' } }}>
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {isRepaymentEligible && (
                            <Tooltip title="Process Repayment">
                              <IconButton size="small" onClick={() => handleOpenRepaymentDialog(invoice)}
                                sx={{ bgcolor: 'success.50', '&:hover': { bgcolor: 'success.100' } }}>
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </StyledTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {usePagination && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: 1, borderColor: 'divider' }}>
            <Pagination
              count={Math.ceil((data?.total || 0) / itemsPerPage)}
              page={page + 1}
              onChange={(event, value) => setPage(value - 1)}
              color="primary"
              showFirstButton
              showLastButton
              sx={{ '& .MuiPaginationItem-root': { borderRadius: 2, fontWeight: 600 } }}
            />
          </Box>
        )}
      </Card>

      {/* BlitzPay Merge Dialog */}
      <Dialog
        open={mergeDialogOpen}
        onClose={handleCloseMergeDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <MergeTypeIcon />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Generate Merged BlitzPay PDF
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Admin-only merged export for funded BlitzPay records under the same client and PO
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={2}>
            {mergeValidationError ? (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                {mergeValidationError}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                This generates one client-facing merged PDF from the selected funded invoices.
                No new invoice record is created, and the original funded invoices stay unchanged.
              </Alert>
            )}

            {mergeConflict && (
              <Alert
                severity="warning"
                sx={{ borderRadius: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleDissolveAndRemerge}
                    disabled={dissolveBlitzPayMergeMutation.isLoading || mergeBlitzPayMutation.isLoading}
                  >
                    {dissolveBlitzPayMergeMutation.isLoading ? "Dissolving..." : "Dissolve & Re-merge"}
                  </Button>
                }
              >
                Invoice #{mergeConflict.invoice_id} is already on merged export{" "}
                MERGED-{String(mergeConflict.merge_group_id).slice(0, 8).toUpperCase()}
                {" "}(invoices {(mergeConflict.source_invoice_ids || []).join(", ")}).
                Reselect that exact set to re-download it, or dissolve it and merge the new selection.
              </Alert>
            )}

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>
                      CLIENT
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedHasMissingClientIdentity ? "Missing client" : selectedClientLabel}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>
                      STATUS
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                      {selectedStatuses[0] || "Mixed"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>
                      EXTERNAL PO
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedExternalPoNumbers.length > 1 ? "Mixed" : selectedExternalPoNumbers[0] || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>
                      SOURCE INVOICES
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {selectedIds.join(", ")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="textSecondary" fontWeight={600}>
                      COMBINED TOTAL
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      ${selectedMergeTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {selectedInvoices.length > 0 && (
              <Stack spacing={1}>
                {selectedInvoices.map((invoice) => (
                  <Stack
                    key={invoice.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1 }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        #{invoice.id} · {invoice.po_number || "No PO"}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {getInvoiceMergeClientLabel(invoice) || "No client identity"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700}>
                      ${(invoice.amount_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider', gap: 1 }}>
          <Button
            onClick={handleCloseMergeDialog}
            disabled={mergeBlitzPayMutation.isLoading || dissolveBlitzPayMergeMutation.isLoading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMergeBlitzPayInvoices}
            disabled={Boolean(mergeValidationError) || mergeBlitzPayMutation.isLoading || dissolveBlitzPayMergeMutation.isLoading}
            startIcon={mergeBlitzPayMutation.isLoading ? <CircularProgress size={16} /> : <MergeTypeIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#f97316',
              '&:hover': {
                backgroundColor: '#ea580c',
              },
              '&.Mui-disabled': {
                backgroundColor: '#fdba74',
                color: 'rgba(255, 255, 255, 0.8)',
              },
            }}
          >
            {mergeBlitzPayMutation.isLoading ? "Generating..." : "Generate Merged PDF"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── External PO Dialog ── */}
      <Dialog
        open={externalPoDialogOpen}
        onClose={handleCloseExternalPoDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ bgcolor: 'warning.50', borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AssignmentIcon color="warning" />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {externalPoInvoice?.external_po_number ? 'Edit' : 'Add'} External PO
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Blitz PO: {externalPoInvoice?.po_number} — Invoice #{externalPoInvoice?.id}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Map your ERP/AP purchase order number to this Blitz invoice for reconciliation.
              Leave blank to clear the existing value.
            </Alert>

            <TextField
              autoFocus
              fullWidth
              label="External / ERP PO Number"
              placeholder="e.g. SAP-12345, PO-98765"
              value={externalPoInput}
              onChange={(e) => setExternalPoInput(e.target.value)}
              inputProps={{ maxLength: 255 }}
              helperText={`${externalPoInput.length}/255 characters`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !updateExternalPoMutation.isLoading) {
                  handleSaveExternalPo();
                }
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {externalPoInvoice?.external_po_number && (
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="textSecondary">
                  Current value: <strong>{externalPoInvoice.external_po_number}</strong>
                </Typography>
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider', gap: 1 }}>
          {/* Clear button only shown when a value exists */}
          {externalPoInvoice?.external_po_number && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                setExternalPoInput("");
                // immediately save as cleared
                updateExternalPoMutation.mutate({
                  invoiceId: externalPoInvoice.id,
                  externalPoNumber: null,
                });
              }}
              disabled={updateExternalPoMutation.isLoading}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, mr: 'auto' }}
            >
              Clear PO
            </Button>
          )}
          <Button
            onClick={handleCloseExternalPoDialog}
            disabled={updateExternalPoMutation.isLoading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveExternalPo}
            disabled={updateExternalPoMutation.isLoading}
            startIcon={updateExternalPoMutation.isLoading && <CircularProgress size={16} />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f6a820 0%, #f05f22 100%)',
            }}
          >
            {updateExternalPoMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ bgcolor: 'success.50', borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon color="success" />
            <Typography variant="h6" fontWeight={700}>Approval Details</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedApproval && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>APPROVED BY</Typography>
                <Typography variant="h6" fontWeight={600} mt={0.5}>{selectedApproval.approved_by_name}</Typography>
                <Typography variant="body2" color="textSecondary">{selectedApproval.approved_by_email}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>DATE CONFIRMED</Typography>
                <Typography variant="body1" mt={0.5}>{new Date(selectedApproval.date_confirmed).toLocaleString()}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>APPROVAL REASONING</Typography>
                <Typography variant="body1" mt={0.5}>{selectedApproval.reasoning}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setApprovalDialogOpen(false)} variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* BlitzPay Repayment Dialog */}
      <Dialog open={repaymentDialogOpen} onClose={handleCloseRepaymentDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ bgcolor: 'secondary.50', borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <PaymentIcon color="secondary" sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>Process BlitzPay Repayment</Typography>
              <Typography variant="caption" color="textSecondary">
                Invoice #{selectedRepaymentInvoice?.po_number}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {selectedRepaymentInvoice && (
            <Stack spacing={3}>
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>Invoice Details</AlertTitle>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>Amount:</strong> ${selectedRepaymentInvoice.amount_due.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Typography variant="body2"><strong>Status:</strong> {selectedRepaymentInvoice.status}</Typography>
                  <Typography variant="body2"><strong>Type:</strong> {selectedRepaymentInvoice.invoice_type}</Typography>
                </Stack>
              </Alert>

              <TextField
                label="Transaction ID" fullWidth required
                value={repaymentTransactionId}
                onChange={(e) => setRepaymentTransactionId(e.target.value)}
                placeholder="e.g., stripe_ch_abc123"
                helperText="External payment transaction reference"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <TextField
                label="Payment Method" fullWidth required select
                value={repaymentPaymentMethod}
                onChange={(e) => setRepaymentPaymentMethod(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>{method.label}</MenuItem>
                ))}
              </TextField>

              <TextField
                label="Notes (Optional)" fullWidth multiline rows={3}
                value={repaymentNotes}
                onChange={(e) => setRepaymentNotes(e.target.value)}
                placeholder="Additional notes about this repayment"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleCloseRepaymentDialog} disabled={processRepaymentMutation.isLoading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleProcessSingleRepayment}
            disabled={!repaymentTransactionId.trim() || !repaymentPaymentMethod || processRepaymentMutation.isLoading}
            startIcon={processRepaymentMutation.isLoading && <CircularProgress size={16} />}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            {processRepaymentMutation.isLoading ? "Processing..." : "Process Repayment"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Invoice Dialog */}
      {isInvoiceDialogOpen && (
        <InvoiceDialog
          open={isInvoiceDialogOpen}
          onClose={handleDialogClose}
          invoiceInfo={editingInvoice}
        />
      )}
    </Box>
  );
};

export default InvoiceDetailsView;
